import React, { useState, useEffect } from 'react';
import BaseModal from '../base/BaseModal';
import { Clock, Paperclip, X, FileText, CheckCircle2 } from 'lucide-react';
import { ExtensionService } from '../../services/extensionService';
import { showToast } from '../../utils/toastNotification';
import DateTimePicker from '../../utils/datePicker';
import { getEffectiveDeadline } from '../../utils/badgeUtils';
import dayjs from 'dayjs';

const getReportCurrentDeadline = (rpt) => {
  if (!rpt) return null;
  return (
    getEffectiveDeadline(rpt) ||
    rpt.ngay_du_kien_hoan_thanh ||
    rpt.han_xu_ly ||
    rpt.thoi_gian_phan_hoi_du_kien ||
    rpt.currentDeadline ||
    rpt.oldDeadline
  );
};

export default function ReportExtensionModal({
  isOpen,
  onClose,
  report,
  onSuccess
}) {
  const [requestedDeadline, setRequestedDeadline] = useState(null);
  const [reason, setReason] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Reset form when opened with new report
  useEffect(() => {
    if (isOpen && report) {
      const currentDl = getReportCurrentDeadline(report);
      const initialNextDl = currentDl
        ? dayjs(currentDl).add(3, 'day')
        : dayjs().add(3, 'day');

      setRequestedDeadline(initialNextDl);
      setReason('');
      setAttachments([]);
      setErrors({});
    }
  }, [isOpen, report]);

  if (!isOpen || !report) return null;

  const currentDeadline = getReportCurrentDeadline(report);
  const currentDeadlineFormatted = currentDeadline
    ? dayjs(currentDeadline).format('DD/MM/YYYY HH:mm')
    : 'Chưa thiết lập';

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (attachments.length + files.length > 5) {
      showToast.error('Chỉ được đính kèm tối đa 5 tệp tài liệu');
      return;
    }

    const newAttachments = files.map((file, idx) => ({
      id: `file-${Date.now()}-${idx}`,
      name: file.name,
      size: `${(file.size / (1024 * 1024)).toFixed(2)} MB`,
      file
    }));

    setAttachments(prev => [...prev, ...newAttachments]);
  };

  const handleRemoveAttachment = (id) => {
    setAttachments(prev => prev.filter(att => att.id !== id));
  };

  const validate = () => {
    const newErrors = {};

    if (!requestedDeadline) {
      newErrors.requestedDeadline = 'Vui lòng chọn thời hạn mới đề xuất';
    } else if (currentDeadline && dayjs(requestedDeadline).isBefore(dayjs(currentDeadline))) {
      newErrors.requestedDeadline = 'Thời hạn đề xuất mới phải sau thời hạn hiện tại';
    } else if (dayjs(requestedDeadline).isBefore(dayjs())) {
      newErrors.requestedDeadline = 'Thời hạn đề xuất không được ở quá khứ';
    }

    if (!reason.trim()) {
      newErrors.reason = 'Vui lòng nhập lý do xin gia hạn xử lý';
    } else if (reason.trim().length < 10) {
      newErrors.reason = 'Lý do xin gia hạn quá ngắn (tối thiểu 10 ký tự)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const extensionPayload = {
        complaintId: report.id || report.ma_phan_anh,
        complaintCode: report.ma_phan_anh || `#PA-${report.id?.slice(0, 6)}`,
        complaintTitle: report.tieu_de || 'Phản ánh kiến nghị',
        category: report.linh_vuc_phan_anh?.ten || report.linh_vuc?.ten || 'Hạ tầng đô thị',
        neighborhood: report.khu_pho || 'KP 01',
        department: report.to_phu_trach?.ho_va_ten || 'Cán bộ phụ trách',
        urgency: report.muc_do || 'Thông thường',
        oldDeadline: currentDeadline || new Date().toISOString(),
        requestedDeadline: requestedDeadline.toISOString(),
        reason: reason.trim(),
        attachments: attachments.map(a => ({ id: a.id, name: a.name, size: a.size }))
      };

      await ExtensionService.create(extensionPayload);
      showToast.success('Đã gửi đề nghị gia hạn thành công. Phản ánh đã chuyển sang trang Quản lý gia hạn.');
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      showToast.error(err.message || 'Gửi đề nghị gia hạn thất bại');
    } finally {
      setIsSubmitting(false);
    }
  };

  const footer = (
    <div className="flex items-center justify-end gap-2.5 w-full">
      <button
        type="button"
        onClick={onClose}
        disabled={isSubmitting}
        className="px-4 py-2 rounded-md text-xs md:text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
      >
        Hủy bỏ
      </button>
      <button
        type="button"
        onClick={handleSubmit}
        disabled={isSubmitting}
        className="px-5 py-2 rounded-md text-xs md:text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 shadow-sm transition-colors cursor-pointer flex items-center gap-1.5"
      >
        {isSubmitting ? (
          <span className="inline-block animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
        ) : (
          <CheckCircle2 className="w-4 h-4" />
        )}
        <span>Gửi đề nghị gia hạn</span>
      </button>
    </div>
  );

  // Khóa tất cả các ngày trước thời điểm hiện tại
  const disabledDate = (current) => {
    return current && current < dayjs().startOf('day');
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Đề nghị gia hạn thời hạn xử lý phản ánh"
      subtitle={`Phản ánh: #${report.ma_phan_anh || report.id}`}
      size="2xl"
      footer={footer}
    >
      <form onSubmit={handleSubmit} className="space-y-4 py-1">
        {/* Tóm tắt thông tin */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3.5 space-y-1.5 text-xs md:text-sm">
          <div className="flex items-center justify-between text-gray-700">
            <span className="text-gray-500">Tiêu đề phản ánh:</span>
            <span className="font-semibold text-gray-900 text-right max-w-xs truncate">{report.tieu_de}</span>
          </div>
          <div className="flex items-center justify-between text-gray-700">
            <span className="text-gray-500">Lĩnh vực & Khu phố:</span>
            <span className="font-medium text-gray-800">
              {report.linh_vuc_phan_anh?.ten || 'Chưa phân loại'} • {report.khu_pho || 'N/A'}
            </span>
          </div>
        </div>

        {/* Thời hạn hiện tại & Thời hạn đề xuất */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5">
              Thời hạn hiện tại
            </label>
            <div className="h-9 bg-gray-100 border border-gray-200 rounded-md px-3 flex items-center gap-2 text-xs md:text-sm text-gray-600">
              <Clock className="w-4 h-4 text-gray-400" />
              <span className="font-medium">{currentDeadlineFormatted}</span>
            </div>
          </div>

          <div>
            <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5">
              Thời hạn mới đề xuất <span className="text-red-500">*</span>
            </label>
            <DateTimePicker
              value={requestedDeadline}
              onChange={(dateObj) => {
                setRequestedDeadline(dateObj);
                if (errors.requestedDeadline) {
                  setErrors(prev => ({ ...prev, requestedDeadline: null }));
                }
              }}
              disabledDate={disabledDate}
              showTime={{ format: 'HH:mm', minuteStep: 5, use12Hours: false }}
              format="DD/MM/YYYY HH:mm"
              placeholder="Chọn ngày và giờ đề xuất mới"
              style={{
                width: '100%',
                height: '36px',
                backgroundColor: '#ffffff',
                border: errors.requestedDeadline ? '1px solid #ef4444' : '1px solid #e5e7eb',
                borderRadius: '0.375rem'
              }}
            />
            {errors.requestedDeadline && (
              <p className="text-xs text-red-500 mt-1">{errors.requestedDeadline}</p>
            )}
          </div>
        </div>

        {/* Lý do xin gia hạn */}
        <div>
          <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5">
            Lý do xin gia hạn <span className="text-red-500">*</span>
          </label>
          <textarea
            rows={4}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (errors.reason) {
                setErrors(prev => ({ ...prev, reason: null }));
              }
            }}
            placeholder="Nhập giải trình lý do xin gia hạn (ví dụ: Chờ đơn vị điện lực phối hợp di dời cột điện hạ thế, thời tiết mưa bão kéo dài...)"
            className={`w-full p-3 bg-white border rounded-md text-xs md:text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none ${
              errors.reason ? 'border-red-500' : 'border-gray-200'
            }`}
          />
          {errors.reason && (
            <p className="text-xs text-red-500 mt-1">{errors.reason}</p>
          )}
        </div>

        {/* Đính kèm tệp */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs md:text-sm font-semibold text-gray-700">
              Tài liệu / Hình ảnh minh chứng đính kèm (tối đa 5 tệp)
            </label>
            <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-semibold transition-colors">
              <Paperclip className="w-3.5 h-3.5" />
              <span>Đính kèm tệp</span>
              <input
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx"
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
          </div>

          {attachments.length > 0 ? (
            <div className="space-y-1.5 mt-2">
              {attachments.map((att) => (
                <div
                  key={att.id}
                  className="flex items-center justify-between px-3 py-2 bg-gray-50 border border-gray-200/80 rounded-lg text-xs text-gray-700"
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1 pr-2">
                    <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span className="font-medium truncate">{att.name}</span>
                    <span className="text-gray-400 flex-shrink-0">({att.size})</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveAttachment(att.id)}
                    className="p-1 hover:bg-gray-200 rounded text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[11px] text-gray-400 italic">
              Chưa có tài liệu đính kèm. Hỗ trợ định dạng: .pdf, .doc, .docx, .jpg, .jpeg, .png, .xlsx.
            </p>
          )}
        </div>
      </form>
    </BaseModal>
  );
}